import { CombinedGraphQLErrors } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { type FunctionComponent, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
	type ProjectRoomFieldsFragment,
	type CreateProjectMutation,
	type CreateProjectMutationVariables
} from '../../../__generated__/graphql-client-types';
import { buildGTMProjectInteractionTracking } from '../../../helpers/analytics/gtm/gtm-utils.helper';
import { focusFirstFocusable } from '../../../helpers/dom/focus.helper';
import {
	getValidationErrorsFromResponse,
	projectNameInputRegistrationConfig
} from '../../../helpers/form-validation-helper/form-validation-helper';
import { useTrackEvent } from '../../../hooks/analytics/analytics.hooks';
import { CREATE_PROJECT } from '../../../queries/project/project.queries';
import { type CreateProjectDrawerFormInputs } from '../../../types/project.types';
import { StyledButton } from '../../buttons';
import { InputMessage } from '../../common-components/input-message/input-message.component';
import { TextInput } from '../../inputs/input/text-input/text-input.component';
import { InputGroup } from '../../inputs/input-group/input-group.component';

export type ProjectNameInputProps = {
	selectedRooms: number[];
	projectCreated: (projectId: number, projectName: string) => void;
	rooms?: ProjectRoomFieldsFragment[];
	trackingPageName?: string;
	title: string;
	titleClassName?: string;
	containerClassName?: string;
};

export const ProjectNameInput: FunctionComponent<ProjectNameInputProps> = ({
	selectedRooms,
	projectCreated,
	rooms,
	trackingPageName,
	title,
	titleClassName = '',
	containerClassName = ''
}) => {
	const [createProject] = useMutation<CreateProjectMutation, CreateProjectMutationVariables>(CREATE_PROJECT);
	const [invalidUserInput, setInvalidUserInput] = useState<Record<string, string>>({});
	const track = useTrackEvent();

	useEffect(() => {
		focusFirstFocusable();
	}, []);

	const onSubmit = async (formData: CreateProjectDrawerFormInputs) => {
		const { data, error } = await createProject({
			variables: {
				name: formData.name,
				roomIds: selectedRooms
			}
		});
		if (CombinedGraphQLErrors.is(error)) setInvalidUserInput(getValidationErrorsFromResponse(error.errors));
		const selectedRoomsString =
			rooms
				?.filter((room) => selectedRooms.includes(room.id))
				.map((room) => room.name)
				.join(',') || '';
		track({
			...buildGTMProjectInteractionTracking(trackingPageName || '', 'new_project_created'),
			roomType: selectedRoomsString
		});

		if (data?.createProject) {
			projectCreated(data.createProject, formData.name);
		}
	};

	const checkField = (fieldName: string) => {
		return invalidUserInput[fieldName] || errors[fieldName]?.message;
	};

	const {
		register,
		handleSubmit,
		formState: { errors }
	} = useForm<CreateProjectDrawerFormInputs>({ mode: 'onChange' });

	return (
		<div className={`${containerClassName} bt b--theme-grey-light pb5`}>
			<div className={`${titleClassName} pv3 lh-title font-medium tl`}>{title}</div>
			<form className="flex flex-column" aria-label="create project form" onSubmit={handleSubmit(onSubmit)}>
				<InputGroup className="flex flex-column w-100" data-testid="new-group-input">
					<div className="flex">
						<div className="flex flex-grow-1">
							<TextInput
								ariaLabel="Project Name"
								automationHook="project-name"
								data-testid="projectNameInput"
								invalid={Boolean(checkField('name'))}
								{...register('name', projectNameInputRegistrationConfig)}
								placeholder="Project Name"
							/>
						</div>
						<StyledButton buttonType="submit" automationHook="create-project">
							Create
						</StyledButton>
					</div>
					<div className="tl">
						<InputMessage invalid={Boolean(checkField('name'))} invalidMessage={checkField('name')} />
					</div>
				</InputGroup>
			</form>
		</div>
	);
};
